import React, { useState, useCallback, useEffect } from "react";
import { FlatList, StyleSheet, View } from "react-native";
import FieldDisplay from "./FieldDisplay";
import { Card } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import { useAppSelector } from "../redux/store";
import DataTableView from "./DataTableView";
import {
  Field,
  OrderByItem,
  TableResponsiveConfig,
} from "../config/companies/config";

export type ListPresentation = "table" | "list";

interface CollectionType {
  presentation?: ListPresentation | ListPresentation[];
  uniqIdNumber?: boolean;

  [key: string]: any;
}

interface DataItem {
  id: string | number;

  [key: string]: any;
}

interface Props {
  fields: Field[];
  data: DataItem[];
  collection: CollectionType;
  presentation: ListPresentation;
  orderBy?: OrderByItem[];
  tableResponsive?: TableResponsiveConfig;
  onLoadMore?: (increment: number) => void;
  onLoadAll?: () => void;
  hasLimit?: boolean;
  totalCount?: number | null;
}

const DynamicDisplay = (props: Props) => {
  const {
    fields,
    data,
    collection,
    presentation,
    orderBy,
    tableResponsive,
    onLoadMore,
    onLoadAll,
    hasLimit,
    totalCount,
  } = props;
  const [viewMode, setViewMode] = useState<ListPresentation>(
    presentation || "list",
  );
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const [contentLayoutWidth, setContentLayoutWidth] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const role = useAppSelector((state) => state.auth.user.role);

  const navigateToDetailsScreen = (
    fields: Field[],
    data: DataItem,
    collection: CollectionType,
  ) => {
    // @ts-ignore - игнорируем ошибку типизации навигации
    navigation.navigate("DetailsScreen", {
      fields: fields,
      data: data,
      collection: collection,
    });
  };

  const getRowStyle = (item: DataItem, fields: Field[]) => {
    for (const field of fields) {
      if (field.rowStyleCondition) {
        const {
          operator,
          value: conditionValue,
          style,
        } = field.rowStyleCondition;
        const fieldValue = item[field.name];

        if (!fieldValue) continue;

        let shouldApplyStyle = false;

        switch (operator) {
          case "past":
            if (fieldValue?.toDate) {
              // Handle Firestore timestamp
              shouldApplyStyle = fieldValue.toDate() < new Date();
            } else if (fieldValue instanceof Date) {
              shouldApplyStyle = fieldValue < new Date();
            } else if (typeof fieldValue === "string") {
              shouldApplyStyle = new Date(fieldValue) < new Date();
            }
            break;
          case "future":
            if (fieldValue?.toDate) {
              shouldApplyStyle = fieldValue.toDate() > new Date();
            } else if (fieldValue instanceof Date) {
              shouldApplyStyle = fieldValue > new Date();
            } else if (typeof fieldValue === "string") {
              shouldApplyStyle = new Date(fieldValue) > new Date();
            }
            break;
          case "equals":
            shouldApplyStyle = fieldValue === conditionValue;
            break;
          case "not_equals":
            shouldApplyStyle = fieldValue !== conditionValue;
            break;
          case "greater_than":
            shouldApplyStyle = fieldValue > conditionValue;
            break;
          case "less_than":
            shouldApplyStyle = fieldValue < conditionValue;
            break;
        }

        if (shouldApplyStyle) {
          return style;
        }
      }
    }
    return {};
  };

  const permittedFields = fields.filter(
    (field) => field.list && field.list.includes(role),
  );

  // Reset loading state when data changes
  useEffect(() => {
    if (isLoadingMore) {
      setIsLoadingMore(false);
    }
  }, [data.length]);

  // Handle infinite scroll for FlatList
  const handleEndReached = useCallback(() => {
    if (
      !onLoadMore ||
      !hasLimit ||
      isLoadingMore ||
      (totalCount !== null &&
        totalCount !== undefined &&
        totalCount > 0 &&
        data.length >= totalCount)
    ) {
      return;
    }

    // Check if we need more data
    const increment = 20; // Load 20 more items

    setIsLoadingMore(true);
    onLoadMore(increment);
  }, [onLoadMore, hasLimit, isLoadingMore, totalCount, data.length]);

  const renderContent = (viewMode: string) => {
    if (viewMode === "table") {
      return (
        <DataTableView
          fields={fields}
          data={data}
          collection={collection}
          orderBy={orderBy}
          tableResponsive={tableResponsive}
          permittedFields={permittedFields}
          getRowStyle={getRowStyle}
          navigateToDetailsScreen={navigateToDetailsScreen}
          onLoadMore={onLoadMore}
          onLoadAll={onLoadAll}
          hasLimit={hasLimit}
          totalCount={totalCount}
        />
      );
    } else if (viewMode === "list") {
      let numColumns = 1;
      return (
        <FlatList
          data={data}
          onLayout={(e) => setContentLayoutWidth(e.nativeEvent.layout.width)}
          keyExtractor={(item) => item.id.toString()}
          numColumns={numColumns}
          contentContainerStyle={{ paddingBottom: 20 }}
          columnWrapperStyle={numColumns > 1 ? { columnGap: 16 } : undefined}
          showsVerticalScrollIndicator={false}
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.5}
          renderItem={({ item, index }) => {
            return (
              <Card
                style={{
                  marginBottom: 10,
                  paddingTop: 10,
                  flex: 1,
                  maxWidth: contentLayoutWidth / numColumns - 16,
                }}
                testID={`card-${index}`}
                key={item.id}
                onPress={() =>
                  navigateToDetailsScreen(fields, item, collection)
                }
              >
                {collection.uniqIdNumber && (
                  <Card.Title title={`ID ${item["numberId"]}`} />
                )}
                <Card.Content>
                  {permittedFields.map((field) => (
                    <FieldDisplay
                      key={field.name}
                      field={field}
                      value={item[field.name]}
                      item={item}
                    />
                  ))}
                </Card.Content>
              </Card>
            );
          }}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <View style={[styles.container, { paddingBottom: insets.bottom }]}>
      {/*{presentationModes && (*/}
      {/*  <View style={{ maxWidth: 200 }}>*/}
      {/*    <SegmentedButtons*/}
      {/*      value={viewMode}*/}
      {/*      onValueChange={(val) => setViewMode(val as ListPresentation)}*/}
      {/*      buttons={presentationModes}*/}
      {/*      style={styles.segmentedButtons}*/}
      {/*    />*/}
      {/*  </View>*/}
      {/*)}*/}
      {renderContent(viewMode)}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 15,
    paddingTop: 0,
  },
  segmentedButtons: {
    marginBottom: 15,
  },
  horizontalScroll: {
    marginBottom: 20,
  },
  dataTable: {
    marginBottom: 5,
    borderWidth: 1,
    borderRadius: 8,
  },
  dataTableTitle: {
    // marginRight: DATA_TABLE_GAP,
    // width: 150,
  },
  dataTableCell: {
    width: 150,
    marginRight: 10,
    overflow: "scroll",
  },

  pressable: {
    flex: 1,
  },
});

export default DynamicDisplay;
